import type { ScaleLinear } from 'd3-scale';
import { useSpring, animated } from '@react-spring/web';
import { getContrastingTextColor } from '../utils/colors';
import type { GuessTheNumberData } from './GuessTheNumber';

interface DistributionProps {
  fill: string;
  outlineColor: string;
  scores: GuessTheNumberData;
  xScale: ScaleLinear<number, number>;
  radius: number;
  isVisible: boolean;
  disableAnimations: boolean;
}

export const Distribution = ({
  fill,
  outlineColor,
  scores,
  xScale,
  radius,
  isVisible,
  disableAnimations,
}: DistributionProps) => {
  const { r, opacity } = useSpring({
    r: isVisible ? radius : radius / 2,
    opacity: isVisible ? 1 : 0,
    immediate: disableAnimations,
  });

  return (
    <animated.g
      data-testid="guess-the-number-distribution"
      style={{
        opacity,
      }}
    >
      {Object.entries(scores).map(([key, score]) => {
        const index = parseInt(key, 10);
        const fontSize = Math.min(radius * 0.9, 40);

        if (score === 0) {
          return null;
        }

        return (
          <g
            key={`score-${index}`}
            data-testid={`guess-the-number-value-${score}`}
          >
            <animated.circle
              cx={xScale(index)}
              cy={0}
              r={r}
              fill={fill}
              style={{
                transition: 'fill 0.2s',
              }}
              stroke={outlineColor}
              strokeWidth={radius * 0.1}
            />
            <text
              x={xScale(index)}
              y={0}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              fill={getContrastingTextColor(fill)}
              pointerEvents="none"
            >
              {score}
            </text>
          </g>
        );
      })}
    </animated.g>
  );
};
